<!--
  PLAN EPAY — Cheque interno (SISTÈM ENTÈN PÈMAN)
  ------------------------------------------------------------------
  Cheque INTERNO (no bancario) que se imprime en hoja A4 en blanco: por
  eso el diseño COMPLETO (marco, líneas, etiquetas, talón) se dibuja aquí
  en HTML/CSS, a diferencia de los cheques bancarios (check_print.js) que
  solo posicionan texto sobre papel preimpreso vía el API de Windows.

  Uso:
    <internal-check-preview v-model="show" :checks="listaDeCheques" />

  `checks`: arreglo de objetos ya resueltos por el padre (ver campos en
  BLANK). Cada cheque se imprime en su propia página A4. Si el arreglo va
  vacío se muestra el cheque de EJEMPLO (diseño aprobado).

  Los campos opcionales (issuerLong, employeeCode, department, concept,
  requestNumber, internalOrder) se OCULTAN si vienen vacíos. Es genérico:
  sirve para cualquier pago con cheque del banco interno, no solo prestaciones.

  IDIOMA DEL MONTO EN LETRAS: español (DOP), inglés (USD) o francés (HTG,
  gourdes). El francés usa la misma librería `convertir-nombre-lettre` que
  los volantes del sistema de RRHH: "... GOURDES AK 50/100".
-->
<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1040" scrollable>
    <v-card class="pe-shell rounded-lg">
      <!-- Barra: Vista Previa / Imprimir / Cerrar (no se imprime) -->
      <v-toolbar flat class="pe-toolbar px-2">
        <v-toolbar-title class="text-h6 font-weight-bold grey--text text--darken-2">
          Vista Previa {{ list.length > 1 ? `(${list.length} cheques)` : "" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="primary" depressed class="text-none mr-2" @click="printChecks">
          <v-icon left>mdi-printer</v-icon>
          Imprimir
        </v-btn>
        <v-btn depressed class="text-none" @click="$emit('input', false)">
          Cerrar
        </v-btn>
      </v-toolbar>

      <!-- CSS del cheque: fuente única para pantalla e impresión -->
      <component :is="'style'" v-text="checkCss"></component>

      <v-card-text class="pe-preview-scroll pa-6">
        <div ref="printArea">
          <div v-for="(c, i) in list" :key="i" class="plan-epay-check" :class="{ 'pe-page-break': i < list.length - 1 }">
            <!-- ===================== CUERPO DEL CHEQUE ===================== -->
            <div class="pe-body">
              <div class="pe-watermark">PLAN EPAY</div>

              <!-- Cabecera -->
              <div class="pe-header">
                <div class="pe-brand">
                  <div class="pe-logo">{{ c.brandInitials || "PE" }}</div>
                  <div>
                    <div class="pe-brand-name">PLAN EPAY</div>
                    <div class="pe-brand-sub">SISTÈM ENTÈN PÈMAN</div>
                  </div>
                </div>
                <div class="pe-checkmeta">
                  <div class="pe-checkmeta-label">CHÈK NIMEWO</div>
                  <div class="pe-checkmeta-number">{{ c.checkNumber }}</div>
                  <div class="pe-checkmeta-date">Dat: {{ c.date }}</div>
                </div>
              </div>

              <div class="pe-rule"></div>

              <!-- Inite emetè -->
              <div class="pe-issuer">
                <span class="pe-issuer-label">INITE EMETÈ:</span>
                <span class="pe-issuer-short">{{ c.issuerShort }}</span>
                <span v-if="c.issuerLong" class="pe-issuer-long">— {{ c.issuerLong }}</span>
              </div>

              <!-- Peye a -->
              <div class="pe-field pe-field--full">
                <span class="pe-label">PEYE A</span>
                <span class="pe-value pe-value--mono pe-underline">{{ c.payee }}</span>
              </div>

              <!-- Montan an + caja de monto -->
              <div class="pe-amount-row">
                <div class="pe-field pe-field--words">
                  <span class="pe-label">MONTAN AN</span>
                  <span class="pe-value pe-value--words pe-underline">{{ words(c) }}</span>
                </div>
                <div class="pe-amount-box">
                  {{ c.currency }} {{ formatAmount(c.amount) }}
                </div>
              </div>

              <!-- Idantite / Kòd / Dept-Zòn (ocultan columnas vacías) -->
              <div class="pe-triple">
                <div class="pe-field">
                  <span class="pe-label">IDANTITE</span>
                  <span class="pe-value pe-value--mono pe-underline">{{ c.identity }}</span>
                </div>
                <div v-if="c.employeeCode" class="pe-field">
                  <span class="pe-label">KÒD</span>
                  <span class="pe-value pe-value--mono pe-underline">{{ c.employeeCode }}</span>
                </div>
                <div v-if="c.department" class="pe-field">
                  <span class="pe-label">DEPT./ZÒN</span>
                  <span class="pe-value pe-value--mono pe-underline">{{ c.department }}</span>
                </div>
              </div>

              <!-- Konsèp -->
              <div v-if="c.concept" class="pe-field pe-field--full">
                <span class="pe-label">KONSÈP</span>
                <span class="pe-value pe-value--mono pe-underline">{{ c.concept }}</span>
              </div>

              <!-- Firmas -->
              <div class="pe-signatures">
                <div class="pe-sign">PREPARE PA</div>
                <div class="pe-sign">OTORIZE — INITE</div>
                <div class="pe-sign">RESEVWA — BENEFISYÈ</div>
              </div>

              <!-- Línea tipo MICR (segmentos opcionales) -->
              <div class="pe-micr">
                <span class="pe-micr-sep">⑆</span> {{ c.checkNumber }}
                <template v-if="c.requestNumber"><span class="pe-micr-sep">⑆</span> {{ c.requestNumber }}</template>
                <template v-if="c.internalOrder"><span class="pe-micr-sep">⑆</span> {{ c.internalOrder }}</template>
                <span class="pe-micr-sep">⑆</span>
              </div>
            </div>

            <!-- Perforación -->
            <div class="pe-perforation"></div>

            <!-- ===================== TALÓN DE CONTROL ===================== -->
            <div class="pe-stub">
              <div class="pe-stub-title">TALON KONTWÒL — KOPI PLAN EPAY / INITE</div>
              <div class="pe-stub-grid">
                <div><span class="pe-stub-k">Chèk:</span> <b>{{ c.checkNumber }}</b></div>
                <div><span class="pe-stub-k">Dat:</span> <b>{{ c.date }}</b></div>
                <div><span class="pe-stub-k">Inite:</span> <b>{{ c.issuerShort }}</b></div>

                <div><span class="pe-stub-k">Benefisyè:</span> <b>{{ c.payee }}</b></div>
                <div><span class="pe-stub-k">Idantite:</span> <b>{{ c.identity }}</b></div>
                <div><span class="pe-stub-k">Montan:</span> <b>{{ c.currency }} {{ formatAmount(c.amount) }}</b></div>

                <div v-if="c.concept"><span class="pe-stub-k">Konsèp:</span> <b>{{ c.concept }}</b></div>
                <div v-if="c.issuedBy"><span class="pe-stub-k">Emèt pa:</span> <b>{{ c.issuedBy }}</b></div>
                <div v-if="c.requestNumber"><span class="pe-stub-k">Nimewo Demann:</span> <b>{{ c.requestNumber }}</b></div>

                <div v-if="c.internalOrder"><span class="pe-stub-k">Lòd Entèn:</span> <b>{{ c.internalOrder }}</b></div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { montoEnLetras, montoEnLetrasFr, amountToWordsEn } from "@/services/check_print";

// Objeto base: todas las claves que entiende el cheque (vacías por defecto).
const BLANK = {
  brandInitials: "PE",
  checkNumber: "",
  date: "",
  issuerShort: "",
  issuerLong: "",
  payee: "",
  currency: "",
  amount: 0,
  amountWords: "", // si viene, se usa tal cual; si no, se calcula
  identity: "",
  employeeCode: "",
  department: "",
  concept: "",
  issuedBy: "",
  requestNumber: "",
  internalOrder: "",
};

// Cheque de ejemplo = diseño aprobado (se ve al abrir sin `checks`).
const SAMPLE = {
  ...BLANK,
  checkNumber: "COD-000001",
  date: "15/07/2026",
  issuerShort: "CODEVI",
  issuerLong: "Compagnie de Développement Industriel S.A.",
  payee: "Jean Pierre Dubois",
  currency: "HTG",
  amount: 25750.5,
  amountWords: "", // se calcula (francés para HTG)
  identity: "001-2345678-9",
  employeeCode: "EMP-001",
  department: "Atelye Pwodiksyon",
  concept: "Salè kenzèn — Janvye 2026",
  issuedBy: "Operatè CODEVI",
  requestNumber: "COD-2026-0001",
  internalOrder: "DCN-2607000001",
};

export default {
  name: "InternalCheckPreview",
  props: {
    value: { type: Boolean, default: false },
    checks: { type: Array, default: () => [] },
  },
  computed: {
    // Cada cheque recibido se completa sobre BLANK (nunca hereda del ejemplo).
    list() {
      if (!this.checks || !this.checks.length) return [SAMPLE];
      return this.checks.map((c) => ({ ...BLANK, ...c }));
    },
    checkCss() {
      return CHECK_CSS;
    },
  },
  methods: {
    formatAmount(n) {
      return Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    // Monto en letras según la moneda: español (DOP), inglés (USD),
    // francés (HTG/gourdes, misma librería que los volantes de RRHH).
    words(c) {
      if (c.amountWords) return c.amountWords;
      const cur = String(c.currency || "DOP").toUpperCase();
      const amt = Number(c.amount) || 0;
      if (cur === "USD") return amountToWordsEn(amt);
      if (cur === "HTG") return montoEnLetrasFr(amt);
      return montoEnLetras(amt, "PESOS", "PESO");
    },
    // Imprime SOLO los cheques en un iframe aislado (misma CSS que la
    // pantalla) para no arrastrar el shell de la app ni contaminar estilos.
    printChecks() {
      const node = this.$refs.printArea;
      if (!node) return;
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8">` +
          `<title>PLAN EPAY</title>` +
          `<style>${CHECK_CSS}${PRINT_CSS}</style></head>` +
          `<body>${node.innerHTML}</body></html>`
      );
      doc.close();
      const win = iframe.contentWindow;
      win.focus();
      setTimeout(() => {
        win.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 250);
    },
  },
};

// ---------------------------------------------------------------------------
// CSS del cheque. Vive como string JS para reutilizarse tal cual en la vista
// previa (inyectado con <component is="style">) y en el iframe de impresión.
// Todo va namespaced bajo .plan-epay-check para no filtrarse a la app.
// ---------------------------------------------------------------------------
const CHECK_CSS = `
.plan-epay-check {
  --pe-navy: #14315e;
  --pe-red: #b8341f;
  --pe-ink: #2d3748;
  --pe-muted: #718096;
  --pe-line: #cbd5e0;
  --pe-box: #e9f0fb;
  width: 210mm;
  max-width: 100%;
  margin: 0 auto 18px;
  background: #fff;
  color: var(--pe-ink);
  font-family: "Helvetica Neue", Arial, sans-serif;
  box-sizing: border-box;
}
.plan-epay-check * { box-sizing: border-box; }
.plan-epay-check .pe-body {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--pe-line);
  border-left: 6px solid var(--pe-navy);
  border-radius: 10px;
  padding: 26px 34px 18px;
  background: linear-gradient(160deg, #f7fafd 0%, #eef4fb 100%);
}
.plan-epay-check .pe-watermark {
  position: absolute; top: 46%; left: 50%;
  transform: translate(-50%, -50%) rotate(-18deg);
  font-size: 92px; font-weight: 800; letter-spacing: 14px;
  color: rgba(20, 49, 94, 0.05); white-space: nowrap;
  pointer-events: none; user-select: none;
}
.plan-epay-check .pe-body > *:not(.pe-watermark) { position: relative; z-index: 1; }
.plan-epay-check .pe-header { display: flex; justify-content: space-between; align-items: flex-start; }
.plan-epay-check .pe-brand { display: flex; align-items: center; gap: 16px; }
.plan-epay-check .pe-logo {
  width: 58px; height: 58px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #2b527e 0%, #14315e 70%);
  color: #fff; font-weight: 700; font-size: 20px; letter-spacing: 1px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(20,49,94,0.35);
}
.plan-epay-check .pe-brand-name { font-size: 26px; font-weight: 800; color: var(--pe-navy); line-height: 1.05; }
.plan-epay-check .pe-brand-sub { font-size: 11px; letter-spacing: 3px; color: var(--pe-muted); font-weight: 600; }
.plan-epay-check .pe-checkmeta { text-align: right; }
.plan-epay-check .pe-checkmeta-label { font-size: 10px; letter-spacing: 2px; color: var(--pe-muted); font-weight: 600; }
.plan-epay-check .pe-checkmeta-number {
  font-family: "Courier New", monospace; font-size: 30px; font-weight: 700;
  color: var(--pe-red); letter-spacing: 3px; line-height: 1.1;
}
.plan-epay-check .pe-checkmeta-date { font-size: 13px; font-weight: 700; font-style: italic; color: var(--pe-ink); }
.plan-epay-check .pe-rule { height: 1px; background: var(--pe-line); margin: 16px 0 18px; }
.plan-epay-check .pe-issuer {
  display: inline-block; background: var(--pe-box); border: 1px solid #bcd0ee;
  border-radius: 8px; padding: 9px 16px; margin-bottom: 20px;
}
.plan-epay-check .pe-issuer-label { font-weight: 700; color: var(--pe-navy); letter-spacing: 1px; }
.plan-epay-check .pe-issuer-short { font-weight: 800; color: var(--pe-navy); font-size: 17px; margin: 0 6px; }
.plan-epay-check .pe-issuer-long { font-style: italic; color: var(--pe-ink); }
.plan-epay-check .pe-field { display: flex; align-items: baseline; gap: 14px; }
.plan-epay-check .pe-field--full { margin-bottom: 16px; }
.plan-epay-check .pe-label {
  font-size: 12px; font-weight: 700; color: var(--pe-navy);
  letter-spacing: 1px; min-width: 96px; flex: 0 0 auto;
}
.plan-epay-check .pe-value { flex: 1 1 auto; color: var(--pe-ink); font-size: 15px; }
.plan-epay-check .pe-value--mono { font-family: "Courier New", monospace; }
.plan-epay-check .pe-value--words { font-style: italic; }
.plan-epay-check .pe-underline { border-bottom: 1px solid var(--pe-line); padding: 0 4px 3px; min-height: 20px; }
.plan-epay-check .pe-amount-row { display: flex; align-items: flex-end; gap: 24px; margin-bottom: 16px; }
.plan-epay-check .pe-field--words { flex: 1 1 auto; }
.plan-epay-check .pe-amount-box {
  flex: 0 0 auto; border: 2px solid var(--pe-navy); border-radius: 8px;
  padding: 10px 20px; font-family: "Courier New", monospace;
  font-size: 22px; font-weight: 700; color: var(--pe-navy);
  white-space: nowrap; background: #fff;
}
.plan-epay-check .pe-triple { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 26px; margin-bottom: 16px; }
.plan-epay-check .pe-triple .pe-label { min-width: auto; }
.plan-epay-check .pe-signatures { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 40px; margin: 40px 6px 10px; }
.plan-epay-check .pe-sign {
  border-top: 1px solid var(--pe-navy); padding-top: 6px; text-align: center;
  font-size: 11px; font-weight: 700; letter-spacing: 1px; color: var(--pe-muted);
}
.plan-epay-check .pe-micr {
  text-align: center; font-family: "Courier New", monospace;
  font-size: 15px; letter-spacing: 4px; color: var(--pe-muted); margin-top: 14px;
}
.plan-epay-check .pe-micr-sep { color: #a0aec0; }
.plan-epay-check .pe-perforation { border-top: 2px dashed var(--pe-line); margin: 14px 0; }
.plan-epay-check .pe-stub { padding: 4px 8px 8px; }
.plan-epay-check .pe-stub-title { font-size: 12px; font-weight: 800; color: var(--pe-navy); letter-spacing: 1px; margin-bottom: 12px; }
.plan-epay-check .pe-stub-grid {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px 24px;
  font-size: 13px; color: var(--pe-ink);
}
.plan-epay-check .pe-stub-k { color: var(--pe-muted); }
`;

// CSS extra SOLO para impresión (A4, un cheque por página).
const PRINT_CSS = `
@page { size: A4 portrait; margin: 12mm; }
html, body { margin: 0; padding: 0; background: #fff; }
.plan-epay-check { width: 100%; margin: 0; }
.plan-epay-check.pe-page-break { page-break-after: always; }
`;
</script>

<style scoped>
.pe-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.pe-preview-scroll {
  background: #f1f4f8;
}
</style>
